import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { UserRole } from '../firebase/schema';
import { AdminData } from '@/types/admin';

export interface AuthUser {
  uid: string;
  email: string;
  displayName: string;
  role: UserRole;
  permissions: string[];
  isAdmin: boolean;
  adminData?: AdminData | null;
  photoURL?: string | null;
}

export const useAuth = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const isAuthenticated = ref(false);

  // Get user role (defaults to 'user' if not set)
  const userRole = computed(() => (user.value?.role?.toLowerCase() || 'user') as UserRole);

  // Login with email and password
  const login = async (email: string, password: string) => {
    try {
      // Import services
      const { authService } = await import('@/firebase/services/authService');
      const { databaseService } = await import('@/firebase/services/databaseService');
      const { adminService } = await import('@/firebase/services/adminService');
      
      // Sign in with Firebase Auth
      const authUser = await authService.signInWithEmail(email, password);
      
      // Get user document from Firestore
      const userDoc = await databaseService.getUserProfile(authUser.uid);
      if (!userDoc) {
        throw new Error('User profile not found');
      }
      
      // Check if user is an admin
      const isAdmin = await adminService.isUserAdmin(authUser.uid);
      const adminData = isAdmin ? await adminService.getAdminUser(authUser.uid) : undefined;
      
      // Set user data with proper null handling
      const userData: AuthUser = {
        uid: authUser.uid,
        email: authUser.email || '',
        role: (isAdmin ? 'admin' : (userDoc.role?.toLowerCase() || 'user')) as UserRole,
        displayName: authUser.displayName || userDoc.displayName || '',
photoURL: authUser.photoURL || userDoc.photoURL || undefined,
        isAdmin: isAdmin,
        permissions: Array.isArray(userDoc.permissions) ? userDoc.permissions : [],
        adminData: adminData || undefined
      };
      
      user.value = userData;
      
      console.log('User logged in:', user.value);
      isAuthenticated.value = true;
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    // Implement actual logout logic here
    isAuthenticated.value = false;
    user.value = null;
  };

  return {
    user,
    isAuthenticated,
    userRole,
    login,
    logout,
  };
});
